import { Alert, Button, Drawer, Input, Space, Tag, Typography, message } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import apiV2 from '../../api/v2'
import { useGenerateCssMutation } from '../../api/v2/gpt'
import { useGetMyRolesQuery } from '../../api/v2/roles'
import { useUpdateSettingsFoMutation } from '../../api/v2/settingsFo'
import { useMyContext } from '../../context/context'
import { useLanguage } from '../../hooks/useLanguages'
import { useSettingsV3 } from '../../hooks/useSettingsV3'
import { mergeCss, parseRules, stringifyRules } from '../../utils/cssMerge'
import { getCssSelector } from '../../utils/cssSelector'

const CUSTOM_CSS_STYLE_ID = 'fo-meeting-custom-css'

const applyCssToDom = css => {
  let el = document.getElementById(CUSTOM_CSS_STYLE_ID)
  if (!css) {
    if (el) el.remove()
    return
  }
  if (!el) {
    el = document.createElement('style')
    el.id = CUSTOM_CSS_STYLE_ID
    document.head.appendChild(el)
  }
  el.textContent = css
}

const HIGHLIGHT_ID = 'design-mode-highlight'
const STYLE_ID = 'design-mode-overlay-styles'

const ensureOverlayStyles = () => {
  if (document.getElementById(STYLE_ID)) return
  const el = document.createElement('style')
  el.id = STYLE_ID
  el.textContent = `
    #${HIGHLIGHT_ID} {
      position: fixed;
      pointer-events: none;
      border: 2px solid #1677ff;
      background: rgba(22, 119, 255, 0.08);
      z-index: 99998;
      transition: all 60ms linear;
      box-sizing: border-box;
    }
    body.design-mode-active * { cursor: crosshair !important; }
    body.design-mode-active .design-mode-ui, body.design-mode-active .design-mode-ui * { cursor: auto !important; }
  `
  document.head.appendChild(el)
}

const ensureHighlightEl = () => {
  let el = document.getElementById(HIGHLIGHT_ID)
  if (!el) {
    el = document.createElement('div')
    el.id = HIGHLIGHT_ID
    document.body.appendChild(el)
  }
  return el
}

const positionHighlight = node => {
  const el = ensureHighlightEl()
  if (!node) {
    el.style.display = 'none'
    return
  }
  const rect = node.getBoundingClientRect()
  el.style.display = 'block'
  el.style.top = `${rect.top}px`
  el.style.left = `${rect.left}px`
  el.style.width = `${rect.width}px`
  el.style.height = `${rect.height}px`
}

const isInsideUi = node => {
  if (!node) return false
  return !!node.closest('.design-mode-ui, .ant-drawer, .ant-modal, .ant-popover, .ant-select-dropdown, .ant-message, .ant-notification')
}

const DesignMode = () => {
  const location = useLocation()
  const requested = useMemo(() => new URLSearchParams(location.search).get('designMode') === '1', [location.search])

  const dispatch = useDispatch()
  const { myContext, event } = useMyContext()
  const { languageId } = useLanguage()
  const { settings } = useSettingsV3()
  const { data: roles } = useGetMyRolesQuery(myContext, { skip: !requested || !myContext?.domainId })

  const enabled = useMemo(() => {
    if (!requested) return false
    if (!roles) return false
    const role = roles.find(f => f.eventId === null || f.eventId === event?.id)
    return !!role?.rights?.includes('CUSTOMIZATIONS_FO_V2')
  }, [requested, roles, event])
  const [generateCss, { isLoading: isGenerating }] = useGenerateCssMutation()
  const [updateSettingsFo, { isLoading: isSaving }] = useUpdateSettingsFoMutation()

  const [picking, setPicking] = useState(false)
  const [hoverNode, setHoverNode] = useState(null)
  const [selector, setSelector] = useState(null)
  const [outerHTML, setOuterHTML] = useState('')
  const [instruction, setInstruction] = useState('')
  const [generated, setGenerated] = useState('')
  const [draft, setDraft] = useState('')

  useEffect(() => {
    if (!enabled) return undefined
    ensureOverlayStyles()
    const root = document.getElementById('root')
    const previousRootWidth = root?.style.width || ''
    const previousRootMarginRight = root?.style.marginRight || ''
    if (root) {
      root.style.width = 'calc(100vw - 420px)'
      root.style.marginRight = '420px'
    }
    return () => {
      if (root) {
        root.style.width = previousRootWidth
        root.style.marginRight = previousRootMarginRight
      }
      const el = document.getElementById(HIGHLIGHT_ID)
      if (el) el.remove()
    }
  }, [enabled])

  useEffect(() => {
    if (!picking) {
      document.body.classList.remove('design-mode-active')
      positionHighlight(null)
      return undefined
    }
    document.body.classList.add('design-mode-active')

    const onMove = e => {
      const node = document.elementFromPoint(e.clientX, e.clientY)
      if (!node || isInsideUi(node)) {
        setHoverNode(null)
        positionHighlight(null)
        return
      }
      setHoverNode(node)
      positionHighlight(node)
    }
    const onClick = e => {
      const node = document.elementFromPoint(e.clientX, e.clientY)
      if (!node || isInsideUi(node)) return
      e.preventDefault()
      e.stopPropagation()
      const sel = getCssSelector(node)
      setSelector(sel)
      setOuterHTML(node.outerHTML?.slice(0, 2000) || '')
      const existingRules = parseRules(settings?.FO_MEETING_CUSTOM_CSS || '')
      const norm = s => s.replace(/\s+/g, ' ').trim()
      const matching = existingRules.filter(r => norm(r.selector) === norm(sel))
      const prefilled = matching.length > 0 ? stringifyRules(matching) : ''
      setDraft(prefilled)
      setGenerated(prefilled)
      setHoverNode(null)
      setPicking(false)
      positionHighlight(node)
    }
    const onKey = e => {
      if (e.key === 'Escape') setPicking(false)
    }
    document.addEventListener('mousemove', onMove, true)
    document.addEventListener('click', onClick, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousemove', onMove, true)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('keydown', onKey, true)
      document.body.classList.remove('design-mode-active')
    }
  }, [picking, settings])

  const onGenerate = useCallback(async () => {
    if (!selector || !instruction.trim()) return
    try {
      const resp = await generateCss({
        selector,
        currentCss: settings?.FO_MEETING_CUSTOM_CSS || '',
        instruction,
        outerHTML,
      }).unwrap()
      setGenerated(resp.css || '')
      setDraft(resp.css || '')
    } catch (err) {
      message.error("Échec de génération CSS")
    }
  }, [selector, instruction, settings, outerHTML, generateCss])

  const onSave = useCallback(async () => {
    if (!draft.trim()) return
    const existing = settings?.FO_MEETING_CUSTOM_CSS || ''
    const next = mergeCss(existing, draft)
    try {
      await updateSettingsFo({ ...myContext, languageId, extended: { FO_MEETING_CUSTOM_CSS: next } }).unwrap()
      applyCssToDom(next)
      dispatch(apiV2.util.invalidateTags(['SETTINGS']))
      setInstruction('')
      message.success('CSS sauvegardé')
    } catch (err) {
      message.error('Échec de sauvegarde')
    }
  }, [draft, settings, myContext, languageId, updateSettingsFo, dispatch])

  if (!enabled) return null

  return (
    <Drawer
      open
      mask={false}
      placement="right"
      width={420}
      closable={false}
      title="Mode design"
      className="design-mode-ui"
      rootClassName="design-mode-ui"
    >
      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Clique sur Sélectionner, puis sur l'élément à styliser. Décris la modification, génère, puis sauvegarde."
        />

        <Space>
          <Button type={picking ? 'default' : 'primary'} onClick={() => setPicking(p => !p)}>
            {picking ? 'Annuler la sélection' : 'Sélectionner un élément'}
          </Button>
          {hoverNode && picking && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {hoverNode.tagName.toLowerCase()}
            </Typography.Text>
          )}
        </Space>

        {selector && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text strong>Sélecteur</Typography.Text>
            <Tag color="blue" style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{selector}</Tag>
          </Space>
        )}

        {selector && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text strong>Que veux-tu modifier ?</Typography.Text>
            <Input.TextArea
              rows={3}
              value={instruction}
              onChange={e => setInstruction(e.target.value)}
              placeholder="Ex : rends ce bouton plus grand, rouge et arrondi"
            />
            <Button
              type="primary"
              onClick={onGenerate}
              loading={isGenerating}
              disabled={!instruction.trim()}
            >
              Générer le CSS
            </Button>
          </Space>
        )}

        {(generated || draft) && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Typography.Text strong>CSS généré (modifiable)</Typography.Text>
            <Input.TextArea
              rows={8}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <Button
              type="primary"
              onClick={onSave}
              loading={isSaving}
              disabled={!draft.trim()}
            >
              Sauvegarder
            </Button>
          </Space>
        )}
      </Space>
    </Drawer>
  )
}

export default DesignMode
